/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool-personeelslabelregel.ts
 * Gegenereerd met Junie (AI), 08-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Regel die filtert op personeelslabels; toepasbaar op zowel Personeel als Planitem.
 */

import { Filtertoolregel } from '../filtertool-regels';
import type { Planitem as PlanitemType } from '../../../models/planitem';
import type { Personeel as PersoneelType } from '../../../models/personeel';
import { PlanitemFilterbareRegel, PersoneelFilterbareRegel } from '../filtertool-capabilities';

/**
 * Regel die matcht wanneer minstens één van de gekozen labels voorkomt.
 * Werkt direct op Personeel of indirect via een Planitem (ingeplande personen).
 */
export class FiltertoolPersoneelslabelRegel extends Filtertoolregel implements PlanitemFilterbareRegel, PersoneelFilterbareRegel {
  /** Gekozen labelnamen. */
  labelNamen: string[];

  constructor(init?: Partial<Filtertoolregel> & { labelNamen?: string[] }) {
    super('personeelslabel', init as any);
    this.labelNamen = Array.isArray(init?.labelNamen) ? init.labelNamen : [];
  }

  /**
   * Geldig wanneer er minstens één labelnaam is gekozen.
   */
  override get valid(): boolean {
    return this.labelNamen?.length > 0;
  }

  /**
   * Serialiseer inclusief labelNamen.
   */
  override toObject(): any {
    return { ...super.toObject(), labelNamen: this.labelNamen || [] };
  }

  /**
   * Past de labelregel toe op een planitem: matcht als één van de ingeplande personeelsleden
   * een van de opgegeven labels heeft. Werkt alleen als het planitem personeelrecords geladen heeft.
   */
  public filterPlanitem(pi: PlanitemType): boolean {
    // Gebruik de reeds gekoppelde personeel-records als die aanwezig zijn.
    const personen = (pi?.personeel || []) as PersoneelType[];
    if (!personen.length) {
      // Geen personen gekoppeld (of niet ingeladen): we kunnen de labels dan niet bepalen.
      // Conservatief: matcht niet.
      return false;
    }
    return personen.some(p => this.filterPersoneel(p));
  }

  /**
   * Past de labelregel toe op een Personeel-object: matcht wanneer dit personeelslid
   * een label draagt dat exact (case-insensitive) voorkomt in de gekozen lijst.
   */
  public filterPersoneel(persoon: PersoneelType): boolean {
    if (!persoon) { return false; }
    const labels = (this.labelNamen || []).map(l => String(l).toLowerCase());
    // Gebruik de helper van Personeel als die aanwezig is; anders fallback via relatie.
    for (const ln of labels) {
      // heeftLabel is case-insensitive volgens implementatie in model
      if ((persoon as any).heeftLabel && persoon.heeftLabel(ln)) {
        return true;
      }
    }
    return false;
  }
}
